'use client';

import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import Cta from "@/components/cta/cta";
import Close from "@/components/icon/close";
import Leaf from "@/components/icon/leaf";
import ZoomControls from "@/components/zoom-controls/zoom-controls";
import { useApp } from '@/context/AppContext';

// Zoom configuration
const ZOOM_STEP = 0.5;
const MAX_ZOOM_STEPS = 5;
const MIN_ZOOM = 1;
const MAX_ZOOM = MIN_ZOOM + (ZOOM_STEP * MAX_ZOOM_STEPS);

export default function RecordViewer({ className = '', image, question, title, text, previewBounds, onClose }) {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isEnterAnimationStarted, setIsEnterAnimationStarted] = useState(false);

  const { isInitialLoad } = useApp()

  const dialogRef = useRef(null);
  const backgroundRef = useRef(null);
  const closeButtonRef = useRef(null);
  const imageContainerRef = useRef(null);
  const imageRef = useRef(null);
  const footerBarRef = useRef(null);
  const titleRef = useRef(null);
  const separatorRef = useRef(null);
  const zoomControlsRef = useRef(null);
  const footerInfoRef = useRef(null);

  // Pan state for dragging
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Calculate max pan bounds for a given zoom level (accounting for object-contain)
  const calculateMaxPan = (zoom) => {
    const container = imageContainerRef.current;
    const imageEl = imageRef.current;
    if (!container || !imageEl) return { x: 0, y: 0 };

    const containerRect = container.getBoundingClientRect();
    const naturalWidth = imageEl.naturalWidth || image?.width;
    const naturalHeight = imageEl.naturalHeight || image?.height;

    if (!naturalWidth || !naturalHeight) return { x: 0, y: 0 };

    // Calculate displayed size with object-contain
    const containerAspect = containerRect.width / containerRect.height;
    const imageAspect = naturalWidth / naturalHeight;
    const isWidthConstrained = imageAspect > containerAspect;

    const displayedWidth = isWidthConstrained ? containerRect.width : containerRect.height * imageAspect;
    const displayedHeight = isWidthConstrained ? containerRect.width / imageAspect : containerRect.height;

    // Max pan is how much the scaled image overflows the container on each side
    return {
      x: Math.max(0, (displayedWidth * zoom - containerRect.width) / 2),
      y: Math.max(0, (displayedHeight * zoom - containerRect.height) / 2),
    };
  };

  // Constrain pan position within bounds
  const constrainPan = (pan, maxPan) => ({
    x: Math.max(-maxPan.x, Math.min(maxPan.x, pan.x)),
    y: Math.max(-maxPan.y, Math.min(maxPan.y, pan.y)),
  });

  const getMaxPan = () => calculateMaxPan(zoomLevel);

  const zoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  };

  const zoomOut = () => {
    setZoomLevel((prev) => {
      const newZoom = Math.max(prev - ZOOM_STEP, MIN_ZOOM);
      if (newZoom === MIN_ZOOM) {
        setPanPosition({ x: 0, y: 0 });
      } else {
        const maxPan = calculateMaxPan(newZoom);
        setPanPosition((prevPan) => constrainPan(prevPan, maxPan));
      }
      return newZoom;
    });
  };

  // Handle scroll wheel pan
  const handleWheel = (e) => {
    if (zoomLevel <= MIN_ZOOM) return;
    e.preventDefault();

    const maxPan = getMaxPan();
    setPanPosition((prev) => constrainPan({ x: prev.x - e.deltaX, y: prev.y - e.deltaY }, maxPan));
  };

  // Get client coordinates from mouse or touch event
  const getClientCoords = (e) => ({
    x: e.touches ? e.touches[0].clientX : e.clientX,
    y: e.touches ? e.touches[0].clientY : e.clientY,
  });

  // Handle drag start
  const handleDragStart = (e) => {
    if (zoomLevel <= MIN_ZOOM) return;
    setIsDragging(true);
    const { x, y } = getClientCoords(e);
    setDragStart({ x: x - panPosition.x, y: y - panPosition.y });
  };

  // Handle drag move
  const handleDragMove = (e) => {
    if (!isDragging || zoomLevel <= MIN_ZOOM) return;
    const { x, y } = getClientCoords(e);
    const maxPan = getMaxPan();
    setPanPosition(constrainPan({ x: x - dragStart.x, y: y - dragStart.y }, maxPan));
  };

  // Handle drag end
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Handle double click to zoom in toward click point
  const handleDoubleClick = (e) => {
    if (zoomLevel >= MAX_ZOOM) return;

    const newZoom = Math.min(zoomLevel + ZOOM_STEP, MAX_ZOOM);
    const container = imageContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    // Offset from container center
    const offsetX = e.clientX - (rect.left + rect.width / 2);
    const offsetY = e.clientY - (rect.top + rect.height / 2);

    // Pan toward the clicked point
    const maxPan = calculateMaxPan(newZoom);
    const newPan = constrainPan({
      x: panPosition.x - offsetX * ZOOM_STEP / newZoom,
      y: panPosition.y - offsetY * ZOOM_STEP / newZoom,
    }, maxPan);

    setZoomLevel(newZoom);
    setPanPosition(newPan);
  };

  const isMobile = () => window.matchMedia('(max-width: 1023px)').matches;

  /** Animates the record viewer entering */
  const enter = () => {
    const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });

    tl.addLabel('start');

    tl.fromTo(backgroundRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3 }, 'start');

    // FLIP animation for image if previewBounds is available
    if (previewBounds && imageContainerRef.current) {
      const finalBounds = imageContainerRef.current.getBoundingClientRect();

      // Calculate the transform needed to position at preview location
      const deltaX = previewBounds.left + previewBounds.width / 2 - (finalBounds.left + finalBounds.width / 2);
      const deltaY = previewBounds.top + previewBounds.height / 2 - (finalBounds.top + finalBounds.height / 2);
      const scale = previewBounds.width / finalBounds.width;

      tl.fromTo(imageContainerRef.current,
        { opacity: 0, x: deltaX, y: deltaY, scale },
        { opacity: 1, x: 0, y: 0, scale: 1, duration: 0.7, ease: 'power3.out', onStart: () => setIsEnterAnimationStarted(true) },
        'start'
      );
    } else {
      tl.fromTo(imageContainerRef.current, { opacity: 0, scale: 0.95 }, { opacity: 1, scale: 1, duration: 0.7, onStart: () => setIsEnterAnimationStarted(true) }, 'start');
    }

    tl.set(closeButtonRef.current, { opacity: 1 }, 'start');

    // Footer bar fade in on mobile only
    if (isMobile()) {
      tl.fromTo(footerBarRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.4 }, 'start');
    }

    tl.fromTo(zoomControlsRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.4 }, 'start+=0.1');
    tl.fromTo(separatorRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.35 }, 'start+=0.1');
    tl.fromTo(footerInfoRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.4 }, 'start+=0.15');
    tl.fromTo(titleRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.4 }, 'start+=0.2');

    return tl;
  };

  /** Animates the record viewer leaving and calls onClose when complete */
  const leave = () => {
    const tl = gsap.timeline({
      defaults: { ease: 'power2.in' },
      onComplete: onClose,
    });

    tl.addLabel('start');

    // Reverse stagger order: title first, then work backwards
    tl.to(titleRef.current, { opacity: 0, y: 20, duration: 0.4 }, 'start');
    tl.to(footerInfoRef.current, { opacity: 0, y: 20, duration: 0.4 }, 'start+=0.1');
    tl.to(separatorRef.current, { opacity: 0, y: 20, duration: 0.35 }, 'start+=0.1');
    tl.to(zoomControlsRef.current, { opacity: 0, y: 20, duration: 0.4 }, 'start+=0.15');

    // Footer bar fade out on mobile only
    if (isMobile()) {
      tl.to(footerBarRef.current, { opacity: 0, y: 20, duration: 0.5 }, 'start+=0.2');
    }

    // Image, close button, and background animate out last
    tl.set(closeButtonRef.current, { opacity: 0 }, 'start+=0.2');

    // FLIP animation for image if previewBounds is available
    if (previewBounds && imageContainerRef.current) {
      const finalBounds = imageContainerRef.current.getBoundingClientRect();
      const deltaX = previewBounds.left + previewBounds.width / 2 - (finalBounds.left + finalBounds.width / 2);
      const deltaY = previewBounds.top + previewBounds.height / 2 - (finalBounds.top + finalBounds.height / 2);
      const scale = previewBounds.width / finalBounds.width;

      tl.to(imageContainerRef.current, { opacity: 0, x: deltaX, y: deltaY, scale, duration: 0.7, ease: 'power3.in' }, 'start+=0.1');
    } else {
      tl.to(imageContainerRef.current, { opacity: 0, scale: 0.95, duration: 0.7 }, 'start+=0.1');
    }

    tl.to(backgroundRef.current, { opacity: 0, duration: 0.3 }, 'start+=0.5');

    return tl;
  };

  useGSAP(() => {
    // Set initial states
    gsap.set([
      backgroundRef.current,
      closeButtonRef.current,
      titleRef.current,
      separatorRef.current,
      zoomControlsRef.current,
      footerInfoRef.current
    ], { opacity: 0 });

    // Only hide image container if no FLIP animation
    if (!previewBounds) {
      gsap.set(imageContainerRef.current, { opacity: 0 });
    }

    // Hide footer bar on mobile only
    if (isMobile()) {
      gsap.set(footerBarRef.current, { opacity: 0 });
    }

    // Play enter animation
    enter();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        leave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    gsap.set(dialogRef.current, {
      y: isInitialLoad ? window.scrollY : 0
    })
  }, [])

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="record-viewer-title"
      className={`fixed inset-0 top-0 left-0 h-screen z-50 flex flex-col ${className}`}
    >
      <div ref={backgroundRef} className="absolute inset-0 bg-[#151515]" aria-hidden="true" />

      <Cta
        ref={closeButtonRef}
        className="fixed z-2 top-4 right-4 cta-light cta-icon-stroke-theme cta-circle-small lg:top-(--space-8) lg:right-(--space-8) lg:cta-circle"
        aria-label="Close record viewer"
        onClick={leave}
      >
        <Close className="w-(--space-7) h-auto" aria-hidden="true" />
      </Cta>

      <div className={`relative flex-1 min-h-0 w-full overflow-hidden lg:absolute lg:left-8 lg:top-[120px] lg:rounded-md lg:w-[calc(100%-64px)] lg:h-[calc(100%-240px)] border transition-[border-color] duration-1000 ${isEnterAnimationStarted ? 'border-[#F5F0E3]/30' : 'border-transparent'}`}>
        {/* Dotted pattern background */}
        <div
          className={`absolute inset-0 pointer-events-none transition-opacity duration-1000 ${isEnterAnimationStarted ? 'opacity-100' : 'opacity-0'}`}
          style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='22' height='22'%3E%3Crect width='1.5' height='1.5' fill='%23717171'/%3E%3C/svg%3E\")",
            backgroundPosition: 'center',
          }}
          aria-hidden="true"
        />

        <div
          ref={imageContainerRef}
          className="relative z-1 w-full h-full flex items-center justify-center overflow-hidden"
          onWheel={handleWheel}
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onDoubleClick={handleDoubleClick}
          style={{ cursor: zoomLevel > MIN_ZOOM ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
        >
          <Image unoptimized={true}
            ref={imageRef}
            alt={image?.alt || ''}
            className="z-1 w-full h-full object-contain select-none pointer-events-none"
            style={{
              transform: `scale(${zoomLevel === 1 ? 0.9 : zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
              transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            }}
            draggable={false}
            priority={true}
            {...image}
          />
        </div>
      </div>

      <div ref={footerBarRef} className="relative z-1 w-full text-white px-(--outer-gutter) pt-(--outer-gutter) pb-11.5 bg-main-dark lg:absolute lg:bottom-0 lg:left-0 lg:h-30 lg:pb-0 lg:pt-0 lg:px-(--space-8) lg:flex lg:bg-transparent items-center">
        <ZoomControls
          ref={zoomControlsRef}
          className="absolute bottom-full right-0 mb-3.5 mr-3.5 lg:relative lg:bottom-0 lg:right-0 lg:mb-0 lg:mr-0"
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          zoomLevel={zoomLevel}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
        />

        <h2 ref={titleRef} id="record-viewer-title" className="select-none heading-5 lg:text-center lg:fixed lg:top-12.75 lg:left-1/2 lg:-translate-x-1/2">
          {title}
        </h2>

        <div ref={separatorRef} className="w-23.25 h-[1px] mt-5.25 mb-6.25 bg-white lg:w-[1px] lg:h-9 lg:mt-0 lg:mb-0 lg:ml-5.25 lg:mr-6" aria-hidden="true" />

        <div ref={footerInfoRef} className="flex gap-x-3.5 items-center lg:gap-x-4.5">
          <div className="flex shrink-0 items-center justify-center rounded-full bg-main-green w-9 h-9 lg:w-11.75 lg:h-11.75" aria-hidden="true">
            <Leaf className="!w-4.75 h-auto text-white" aria-hidden="true" />
          </div>
          <p className="body-2-sans max-w-75">{question}</p>
        </div>
      </div>
    </div>
  )
}
